import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import {
  useApproveExpense,
  useDeleteExpense,
  useExpenseList,
  useRejectExpense,
  type ReimbursementStatus,
} from "../hooks/useExpenses";
import { NewExpenseDrawer } from "../components/NewExpenseDrawer";
import { formatCurrency } from "../components/CurrencyAmount";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useVaultList } from "@/modules/vault/hooks/useVault";
import { useAssetList } from "@/modules/assets/hooks/useAssets";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useContactList } from "@/modules/contacts/hooks/useContacts";
import type { ExpenseListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function ExpenseStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    pending: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Pending" },
    approved: {
      bg: "hsl(142,55%,92%)",
      fg: "hsl(142,72%,28%)",
      label: "Approved",
    },
    rejected: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Rejected" },
  };
  const s = styles[status] ?? styles.pending;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function ReimbursementBadge({ row }: { row: ExpenseListItem }) {
  if (row.reimbursementStatus === "not_required") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}
      >
        N/A
      </span>
    );
  }
  if (row.reimbursementStatus === "pending") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ background: "hsl(38,92%,92%)", color: "hsl(38,92%,30%)" }}
      >
        Pending
      </span>
    );
  }
  if (row.reimbursementStatus === "partial") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }}
        title={`${formatCurrency(row.reimbursedAmount, row.currency)} of ${formatCurrency(row.amount, row.currency)}`}
      >
        Partial ({formatCurrency(row.reimbursedAmount, row.currency)} /{" "}
        {formatCurrency(row.amount, row.currency)})
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }}
    >
      Reimbursed
    </span>
  );
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const EXPENSE_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "category", label: "Category" },
  { key: "subcategory", label: "Subcategory", defaultVisible: false },
  { key: "amount", label: "Amount" },
  { key: "baseAmount", label: "Base (AED)", defaultVisible: false },
  { key: "budget", label: "Budget" },
  { key: "status", label: "Status" },
  { key: "expenseDate", label: "Date" },
  { key: "submittedByName", label: "Submitted By" },
  { key: "paidByName", label: "Paid By" },
  { key: "reimbursementStatus", label: "Reimbursement" },
];

const REIMB_STATUS_OPTIONS: { value: ReimbursementStatus; label: string }[] = [
  { value: "not_required", label: "Not Reimbursable" },
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "complete", label: "Reimbursed" },
];

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";
const iconBtnApprove =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent transition-[background,color] duration-150 hover:bg-[hsl(142,55%,92%)] text-[hsl(142,72%,28%)]";
const iconBtnReject =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] text-[hsl(0,70%,45%)]";

export default function ExpenseListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [paidByFilter, setPaidByFilter] = useState("");
  const [vaultFilter, setVaultFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [assetFilter, setAssetFilter] = useState("");
  const [vendorContactFilter, setVendorContactFilter] = useState("");
  const [reimbStatusFilter, setReimbStatusFilter] = useState<
    ReimbursementStatus[]
  >([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<ExpenseListItem | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ExpenseListItem | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("expenses", EXPENSE_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      paidById: paidByFilter || undefined,
      accessVaultId: vaultFilter || undefined,
      productId: productFilter || undefined,
      assetId: assetFilter || undefined,
      vendorContactId: vendorContactFilter || undefined,
      reimbursementStatus:
        reimbStatusFilter.length > 0 ? reimbStatusFilter : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [
      search,
      statusFilter,
      categoryFilter,
      paidByFilter,
      vaultFilter,
      productFilter,
      assetFilter,
      vendorContactFilter,
      reimbStatusFilter,
      dateFrom,
      dateTo,
    ],
  );

  const { data, isLoading, isError, error } = useExpenseList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    paidById: paidByFilter || undefined,
    accessVaultId: vaultFilter || undefined,
    productId: productFilter || undefined,
    assetId: assetFilter || undefined,
    vendorContactId: vendorContactFilter || undefined,
    reimbursementStatus:
      reimbStatusFilter.length > 0 ? reimbStatusFilter : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const { data: categoryItems = [] } = useCategoryItems("expense_category");
  const { data: usersResult } = useUserList({
    page: 1,
    limit: 100,
    status: "ACTIVE",
  });
  const allUsers = usersResult?.data ?? [];

  const { data: vaultResult } = useVaultList({ page: 1, limit: 100 });
  const vaultEntries = vaultResult?.data ?? [];
  const { data: productsResult } = useProductList({ page: 1, limit: 100 });
  const allProducts = productsResult?.data ?? [];
  const { data: assetsResult } = useAssetList({ page: 1, limit: 100 });
  const allAssets = assetsResult?.data ?? [];
  const { data: contactsResult } = useContactList({ page: 1, limit: 100 });
  const vendorContacts = useMemo(
    () =>
      (contactsResult?.data ?? []).filter(
        (c) =>
          c.id === vendorContactFilter ||
          (c.status === "Active" && c.type === "Vendor"),
      ),
    [contactsResult?.data, vendorContactFilter],
  );

  const approveExpense = useApproveExpense();
  const rejectExpense = useRejectExpense();
  const deleteExpense = useDeleteExpense();

  const expenses = data?.data ?? [];
  const summary = data?.summary;
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const allAed = expenses.length > 0 && expenses.every((e) => e.currency === "AED");

  const owedFilterActive =
    !!paidByFilter &&
    reimbStatusFilter.length > 0 &&
    reimbStatusFilter.every((s) => s === "pending" || s === "partial");

  // Client-side total of what's still owed on the current page of results.
  // Uses baseAmount/reimbursedAmount converted to AED for cross-currency safety.
  const owedTotalAed = useMemo(() => {
    if (!owedFilterActive) return 0;
    return expenses.reduce((sum, e) => {
      if (
        e.reimbursementStatus !== "pending" &&
        e.reimbursementStatus !== "partial"
      )
        return sum;
      const owedNative = Math.max(0, e.amount - e.reimbursedAmount);
      const rate = e.exchangeRate || 1;
      const owedAed = e.currency === e.baseCurrency ? owedNative : owedNative * rate;
      return sum + owedAed;
    }, 0);
  }, [owedFilterActive, expenses]);

  const owedPayerName = useMemo(() => {
    if (!owedFilterActive) return "";
    if (expenses[0]?.paidByName) return expenses[0].paidByName;
    return allUsers.find((u) => u.id === paidByFilter)?.name ?? "selected user";
  }, [owedFilterActive, expenses, allUsers, paidByFilter]);

  const owedCount = expenses.filter(
    (e) =>
      e.reimbursementStatus === "pending" ||
      e.reimbursementStatus === "partial",
  ).length;

  function toggleReimbStatus(s: ReimbursementStatus) {
    setReimbStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    setPage(1);
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  async function handleApprove(e: ExpenseListItem) {
    await approveExpense.mutateAsync(e.id);
    showToast(`Expense ${e.code} approved`);
  }

  async function submitRejection() {
    if (!rejectTarget || !rejectReason.trim()) return;
    await rejectExpense.mutateAsync({
      id: rejectTarget.id,
      rejectionReason: rejectReason.trim(),
    });
    showToast(`Expense ${rejectTarget.code} rejected`);
    setRejectTarget(null);
    setRejectReason("");
  }

  function handleDelete(e: ExpenseListItem) {
    setDeleteTarget({ id: e.id, name: e.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteExpense.mutateAsync(deleteTarget.id);
    showToast("Expense deleted");
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Expenses" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Receipt
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Expenses</h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="expenses"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={EXPENSE_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Expense
            </Button>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div
            className="rounded-xl border bg-white px-4 py-3"
            style={{ borderColor: BORDER }}
          >
            <div className="text-[11px] uppercase text-muted-foreground">
              Total Expenses
            </div>
            <div className="mt-1 text-[20px] font-semibold">
              {summary?.totalCount ?? 0}
            </div>
          </div>
          <div
            className="rounded-xl border bg-white px-4 py-3"
            style={{ borderColor: BORDER }}
          >
            <div className="text-[11px] uppercase text-muted-foreground">
              Pending Amount
            </div>
            <div className="mt-1 text-[20px] font-semibold text-[hsl(38,92%,30%)]">
              {formatCurrency(summary?.pendingAmount ?? 0, "AED")}
            </div>
          </div>
          <div
            className="rounded-xl border bg-white px-4 py-3"
            style={{ borderColor: BORDER }}
          >
            <div className="text-[11px] uppercase text-muted-foreground">
              Approved Amount
            </div>
            <div className="mt-1 text-[20px] font-semibold text-[hsl(142,72%,28%)]">
              {formatCurrency(summary?.approvedAmount ?? 0, "AED")}
            </div>
          </div>
          <div
            className="rounded-xl border bg-white px-4 py-3"
            style={{ borderColor: BORDER }}
          >
            <div className="text-[11px] uppercase text-muted-foreground">
              Rejected Count
            </div>
            <div className="mt-1 text-[20px] font-semibold text-[hsl(0,70%,45%)]">
              {summary?.rejectedCount ?? 0}
            </div>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load expenses</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-3 flex-wrap">
              <div className="relative w-[240px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search expenses..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-36 shrink-0"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Select>
              <Select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                className="w-56 shrink-0"
              >
                <option value="">All Categories</option>
                {categoryItems.map((c) => (
                  <option key={c.id} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-36 shrink-0"
                placeholder="From"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-36 shrink-0"
                placeholder="To"
              />
              <Select
                value={paidByFilter}
                onChange={(e) => {
                  setPaidByFilter(e.target.value);
                  setPage(1);
                }}
                className="w-56 shrink-0"
              >
                <option value="">All Paid By</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
              <Select
                value={vaultFilter}
                onChange={(e) => {
                  setVaultFilter(e.target.value);
                  setPage(1);
                }}
                className="w-56 shrink-0"
              >
                <option value="">All Vault Entries</option>
                {vaultEntries.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.code} — {v.title}
                  </option>
                ))}
              </Select>
              <Select
                value={productFilter}
                onChange={(e) => {
                  setProductFilter(e.target.value);
                  setPage(1);
                }}
                className="w-56 shrink-0"
              >
                <option value="">All Products</option>
                {allProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </Select>
              <Select
                value={assetFilter}
                onChange={(e) => {
                  setAssetFilter(e.target.value);
                  setPage(1);
                }}
                className="w-56 shrink-0"
              >
                <option value="">All Assets</option>
                {allAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </Select>
              <Select
                value={vendorContactFilter}
                onChange={(e) => {
                  setVendorContactFilter(e.target.value);
                  setPage(1);
                }}
                className="w-56 shrink-0"
              >
                <option value="">All Vendor Contacts</option>
                {vendorContacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.fullName}
                    {c.companyName ? ` — ${c.companyName}` : ""}
                  </option>
                ))}
              </Select>
              <div className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-[12px]"
                style={{ borderColor: BORDER }}
              >
                <span className="text-muted-foreground">Reimbursement:</span>
                {REIMB_STATUS_OPTIONS.map((opt) => {
                  const active = reimbStatusFilter.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleReimbStatus(opt.value)}
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                        active
                          ? "bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]"
                          : "text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {owedFilterActive && owedCount > 0 && (
              <div
                className="mb-3 flex items-center justify-between rounded-md border px-4 py-2 text-[13px]"
                style={{
                  borderColor: "hsl(38,92%,80%)",
                  background: "hsl(38,92%,96%)",
                }}
              >
                <span className="text-foreground">
                  Total owed to{" "}
                  <span className="font-semibold">{owedPayerName}</span>:{" "}
                  <span className="font-semibold">
                    {formatCurrency(owedTotalAed, "AED")}
                  </span>{" "}
                  <span className="text-muted-foreground">
                    ({owedCount} unpaid expense{owedCount === 1 ? "" : "s"} on this page)
                  </span>
                </span>
              </div>
            )}

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading expenses…
                </div>
              ) : expenses.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No expenses match the current filters.
                </div>
              ) : (
                <table
                  className="w-full text-[13px]"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("code") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          #
                        </th>
                      )}
                      {isVisible("title") && (
                        <th className="px-[14px] py-[9px]">Title</th>
                      )}
                      {isVisible("category") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Category
                        </th>
                      )}
                      {isVisible("subcategory") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Subcategory
                        </th>
                      )}
                      {isVisible("amount") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
                          Amount
                        </th>
                      )}
                      {isVisible("baseAmount") && !allAed && (
                        <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
                          Base (AED)
                        </th>
                      )}
                      {isVisible("budget") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 160 }}>
                          Budget
                        </th>
                      )}
                      {isVisible("status") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          Status
                        </th>
                      )}
                      {isVisible("expenseDate") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          Date
                        </th>
                      )}
                      {isVisible("submittedByName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 160 }}>
                          Submitted By
                        </th>
                      )}
                      {isVisible("paidByName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 160 }}>
                          Paid By
                        </th>
                      )}
                      {isVisible("reimbursementStatus") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 160 }}>
                          Reimbursement
                        </th>
                      )}
                      <th
                        className="px-[14px] py-[9px] text-right"
                        style={{ width: 170 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {expenses.map((e) => {
                      const pending = e.status === "pending";
                      return (
                        <tr
                          key={e.id}
                          className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                          onClick={() => navigate(`/expenses/${e.id}`)}
                        >
                          {isVisible("code") && (
                            <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                              {e.code}
                            </td>
                          )}
                          {isVisible("title") && (
                            <td className="px-[14px] py-[11px]">
                              <Link
                                to={`/expenses/${e.id}`}
                                onClick={(ev) => ev.stopPropagation()}
                                className="font-medium text-foreground hover:text-[hsl(210,100%,35%)]"
                              >
                                {truncate(e.title, 45)}
                              </Link>
                            </td>
                          )}
                          {isVisible("category") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {e.category ? (
                                <span className="capitalize">
                                  {truncate(e.category.replace(/_/g, " "), 24)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("subcategory") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {e.subcategory ? (
                                <span className="capitalize">
                                  {truncate(e.subcategory.replace(/_/g, " "), 24)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("amount") && (
                            <td className="px-[14px] py-[11px] text-foreground">
                              {formatCurrency(e.amount, e.currency)}
                            </td>
                          )}
                          {isVisible("baseAmount") && !allAed && (
                            <td
                              className={`px-[14px] py-[11px] ${
                                e.currency === "AED"
                                  ? "text-muted-foreground"
                                  : "text-foreground"
                              }`}
                            >
                              {formatCurrency(e.baseAmount, e.baseCurrency)}
                            </td>
                          )}
                          {isVisible("budget") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {e.budgetId ? (
                                <Link
                                  to={`/budgets/${e.budgetId}`}
                                  onClick={(ev) => ev.stopPropagation()}
                                  className="hover:text-[hsl(210,100%,35%)]"
                                >
                                  <span className="font-mono text-[11px]">
                                    {e.budgetCode}
                                  </span>
                                  {e.budgetName ? ` — ${truncate(e.budgetName, 20)}` : ""}
                                </Link>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("status") && (
                            <td className="px-[14px] py-[11px]">
                              <ExpenseStatusBadge status={e.status} />
                            </td>
                          )}
                          {isVisible("expenseDate") && (
                            <td className="px-[14px] py-[11px] text-foreground">
                              {formatDate(e.expenseDate)}
                            </td>
                          )}
                          {isVisible("submittedByName") && (
                            <td className="px-[14px] py-[11px]">
                              {e.submittedByName ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                                    style={{
                                      background: "hsl(210,55%,92%)",
                                      color: "hsl(210,100%,28%)",
                                    }}
                                  >
                                    {initialsOf(e.submittedByName)}
                                  </span>
                                  <span className="text-foreground">
                                    {e.submittedByName}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("paidByName") && (
                            <td className="px-[14px] py-[11px]">
                              {e.paidByName ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                                    style={{
                                      background: "hsl(38,92%,92%)",
                                      color: "hsl(38,92%,30%)",
                                    }}
                                  >
                                    {initialsOf(e.paidByName)}
                                  </span>
                                  <span className="text-foreground">
                                    {e.paidByName}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("reimbursementStatus") && (
                            <td className="px-[14px] py-[11px]">
                              <ReimbursementBadge row={e} />
                            </td>
                          )}
                          <td
                            className="relative px-[14px] py-[11px] text-right"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="View"
                                onClick={() => navigate(`/expenses/${e.id}`)}
                              >
                                <Eye className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Edit"
                                onClick={() => navigate(`/expenses/${e.id}`)}
                              >
                                <Pencil className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Duplicate"
                                onClick={() => setDuplicateTarget(e)}
                              >
                                <Copy className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                              {pending && (
                                <>
                                  <button
                                    type="button"
                                    className={iconBtnApprove}
                                    title="Approve"
                                    disabled={approveExpense.isPending}
                                    onClick={() => handleApprove(e)}
                                  >
                                    <Check
                                      className="h-[15px] w-[15px]"
                                      strokeWidth={1.8}
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className={iconBtnReject}
                                    title="Reject"
                                    onClick={() => {
                                      setRejectTarget(e);
                                      setRejectReason("");
                                    }}
                                  >
                                    <X
                                      className="h-[15px] w-[15px]"
                                      strokeWidth={1.8}
                                    />
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                className={iconBtnDelete}
                                title="Delete"
                                onClick={() => handleDelete(e)}
                              >
                                <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewExpenseDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Expense created: ${code}`)}
      />

      <NewExpenseDrawer
        isOpen={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        duplicateFrom={duplicateTarget ?? undefined}
        onCreated={(code) => showToast(`Expense created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete expense?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Expenses list.`
            : ""
        }
        onConfirm={confirmDelete}
      />

      {rejectTarget && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
          <div
            className="w-[440px] rounded-xl border bg-white p-6 shadow-xl"
            style={{ borderColor: BORDER }}
          >
            <h3 className="text-[15px] font-semibold text-foreground">
              Reject Expense
            </h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {rejectTarget.code} — {rejectTarget.title}
            </p>
            <div className="mt-4 space-y-2">
              <label className="text-[12px] font-medium">
                Reason for rejection <span className="text-destructive">*</span>
              </label>
              <textarea
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Explain why this expense is being rejected..."
                autoFocus
              />
            </div>
            <div className="mt-4 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={submitRejection}
                disabled={!rejectReason.trim() || rejectExpense.isPending}
                className="bg-[hsl(0,70%,50%)] text-white hover:bg-[hsl(0,70%,42%)]"
              >
                Reject Expense
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
